import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';

import Button from '../common/Button';

import { fetchGetAdAttachments, fetchDeleteAdAttachment } from '../../actions/settings';
import { showModal } from '../../actions/modal';

import { getYandexStoreImageMin, getDeliveryBlock, parseError } from '../../helpers';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const AdditionalAttachments = styled('div')`
  .header-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 1.5rem 16px;
    margin: 0 -1.5rem;
    .header-title {
      margin-bottom: 0;
    }
  }

  .no-add-attachments {
    margin-top: 24px;
    font-size: 14px;
  }

  .attachment {
    padding: 14px 14px;
    border-bottom: 1px solid #dee2e6;
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    display: flex;
    justify-content: space-between;
    .attachment-left {
      display: flex;
    }
    .attachment-info {
      h5 {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 3px;
        color: #323a46;
      }
      .attachment-info-id {
        font-size: 14px;
        line-height: 20px;
        color: #b4b9c1;
      }
      .attachment-info-delivery {
        display: flex;
        align-items: center;
        margin-top: 14px;
        > span {
          font-weight: 500;
          font-size: 12px;
          line-height: 12px;
          color: #6e768e;
          margin-right: 8px;
          white-space: nowrap;
        }
        ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          li {
            margin-right: 4px;
            margin-bottom: 4px;
            background: #d3f0ff;
            border-radius: 4px;
            padding: 4px 8px;
            font-size: 12px;
            line-height: 12px;
            color: #264351;
          }
        }
      }
    }
    .attachment-right {
      display: flex;
      margin-top: 25px;
      > div {
        width: 75px;
        display: flex;
        justify-content: center;
        i {
          font-size: 18px;
          color: #98a6ad;
          cursor: pointer;
        }
      }
    }
    .attachment-quantity {
      font-size: 14px;
      line-height: 20px;
      color: #6e768e;
    }
    .image-wrap {
      width: 70px;
    }
    .image-block {
      margin-top: 5px;
      height: 56px;
      width: 56px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: contain;
      border-radius: 3px;
      margin-right: 21px;
    }
  }

  @media (max-width: 1070px) {
    .attachment {
      .attachment-info {
        .attachment-info-delivery {
          flex-direction: column;
          align-items: flex-start;
          > span {
            margin-bottom: 8px;
          }
        }
      }
    }
  }
  @media (max-width: 540px) {
    .attachment {
      flex-direction: column;
      .attachment-right {
        > div {
          justify-content: flex-start;
        }
      }
    }
    .header-block {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

const AdditionalAttachmentsBlock = () => {
  const dispatch = useDispatch();

  const deliveryInfo = useSelector((state) => state.orders.deliveryInfo);
  const [attachments, setAttachments] = useState([]);

  const { t } = useAutoTranslation();

  useEffect(() => {
    fetchAdditionalAttachments();
  }, []);

  const fetchAdditionalAttachments = () => {
    dispatch(fetchGetAdAttachments()).then((adAttachments) => {
      setAttachments(adAttachments);
    });
  };

  const handleEditAdditionalAttachments = (attachment) => {
    if (attachment) {
      dispatch(
        showModal('EditAdditionalAttachments', {
          attachments: attachment,
          handleUpdateAdditionalAttachments: fetchAdditionalAttachments,
        }),
      );
      return;
    }

    dispatch(
      showModal('AddingAdditionalAttachment', {
        handleUpdateAttachmentsItems: handleEditAdditionalAttachments,
        requiredOneItem: true,
      }),
    );
  };

  const handleRemoveAdditionalAttachment = async (attachment) => {
    await dispatch(fetchDeleteAdAttachment(attachment.id))
      .then(() => {
        fetchAdditionalAttachments();
        toast.info(t('Доп. вложение успешно удалено'));
      })
      .catch((err) => {
        toast.error(parseError(err));
      });
  };

  return (
    <AdditionalAttachments>
      <div className="border-bottom header-block">
        <h4 className="header-title">{t('Дополнительные вложения')}</h4>
        <Button
          classType="btn-danger"
          type="button"
          icon="fe-plus"
          text={t('Новое доп.вложение')}
          onClick={() => handleEditAdditionalAttachments(null)}
        />
      </div>

      <div className="attachments-list">
        {attachments.length ? (
          <>
            {attachments.map((attachment) => (
              <div className="attachment">
                <div className="attachment-left">
                  <div className="image-wrap">
                    <div
                      className="image-block"
                      style={{
                        backgroundImage: `url(${getYandexStoreImageMin(attachment.img, 100)})`,
                      }}
                    />
                  </div>
                  <div className="attachment-info">
                    <h5>{attachment.shortName}</h5>
                    <span className="attachment-info-id">{attachment.comment}</span>
                    {attachment.deliveryInfoList?.length ? (
                      <div className="attachment-info-delivery">
                        <span>{t('Каналы доставки')}:</span>
                        <ul>
                          {attachment.deliveryInfoList.map((delivery) => (
                            <li>{getDeliveryBlock(delivery, deliveryInfo)}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="attachment-right">
                  <div className="attachment-quantity">
                    {attachment.quantity} {t('шт')}.
                  </div>
                  <div className="attachment-edit">
                    <i
                      className="fe-edit-2"
                      onClick={() => handleEditAdditionalAttachments([attachment])}
                    ></i>
                  </div>
                  <div className="attachment-remove">
                    <i
                      className="fe-trash"
                      onClick={() => handleRemoveAdditionalAttachment(attachment)}
                    ></i>
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <p className="no-add-attachments text-muted">{t('Нет дополнительных вложений')}</p>
        )}
      </div>
    </AdditionalAttachments>
  );
};

export default AdditionalAttachmentsBlock;
